import React, { useEffect, useRef, useState } from 'react';
import type {
  AdminAccountCostResponse,
  AdminAccountProject,
  AdminPlanTemplate,
} from '@brand24-original/llm-types';
import {
  formatMoney,
  formatUsd,
  getAccountCosts,
} from '../../../../shared/services/admin/costs/AdminCostsService';
import './AdminUsersEditPageV2.scss';
import { useTranslation } from 'react-i18next';
import Loader from '../../../../shared/components/common/loader/Loader';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Sidebar } from 'primereact/sidebar';
import SimpleBar from 'simplebar-react';
import { useForm } from 'react-hook-form';
import {
  addAdminNote,
  banAccount,
  changeUserAccountData,
  deleteAdminNote,
  getPlanTemplates,
  syncMonitoringSchedule,
  getAccountData,
  getAuditLog,
  getUserActivityLog,
  getInvitedUsers,
  getProjectsForAccount,
  impersonateUser,
  unbanAccount,
  updateAdminNote,
  extendBan,
} from '../../../../shared/services/admin/users/AdminUsersService';
import { AdminNoteResponse } from '../../../../shared/models/admin/users/AdminNoteResponse';
import {
  AccountFieldChange,
  AdminAuditLogEntry,
} from '../../../../shared/models/admin/audit/AdminAuditLog';
import { UserActivityLogEntry } from '../../../../shared/models/admin/audit/UserActivityLog';
import { formatBilling } from '../../../../shared/helpers/admin/formatBilling';
import { getTokenData } from '../../../../shared/services/auth/AuthService';
import {
  IMPERSONATION_ORIGINAL_TOKEN_KEY,
  IMPERSONATION_TARGET_ID_KEY,
} from '../../../../shared/constans/constans';
import { useAuth } from '../../../../shared/contexts/auth/AuthContext';
import { useAccount } from '../../../../shared/contexts/account/AccountContexts';
import { AccountDataResponse } from '../../../../shared/models/account/AccountData';
import { ConfirmDialog, confirmDialog, ConfirmDialogReturn } from 'primereact/confirmdialog';
import { InvitedUserResponse } from '../../../../shared/models/admin/users/InvitedUserResponse';
import { format } from 'date-fns';
import AdminInviteUser from '../components/admin-invite-user/AdminInviteUser';
import { DataTable } from '../../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { PROJECTS } from '../../../../shared/routes/path';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import DropdownItem from '../../../../shared/components/dropdown-item/DropdownItem';
import { AccountType } from '../../../../shared/types/account/AccountType';
import { AccountStatus } from '../../../../shared/types/account/AccountStatus';
import { UserAccountData } from './models/UserAccountData';
import { Checkbox } from 'primereact/checkbox';
import { allMonitorTypes } from '../../../../shared/types/monitor/MonitorType';
import { addonDescriptions, allAddons, impliedBy } from '../../../../shared/types/account/Addons';
import {
  MONITORING_SCHEDULE_PRESETS,
  MonitoringSchedulePreset,
  presetOfInterval,
} from '../../../../shared/models/projects/MonitoringInterval';
import { ApiKeyResponse, CreateApiKeyRequest } from '../../../settings/api-keys/models/ApiKeyResponse';
import { createAccountApiKey, getAccountApiKeys } from './services/AdminAccountApiKeysService';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import useToast from '../../../../shared/hooks/global/UseToast';
import {
  AdminResource,
  getAdminPermissions,
} from '../../../../shared/models/admin/permissions/AdminPermission';
import StatTile from '../../../../shared/components/stat-tile/StatTile';
import { getMonitorName } from '../../../../shared/helpers/MonitorTypeHelpers';
import { getMonitorTypeIcon } from '../../../prompt/preview/components/stats/helpers/StatsHelper';
import { MODEL_CATALOG } from './modelCatalog';
import { ACCOUNT_TYPE_HINTS, STATUS_HINTS } from './accountHints';
import AccountPageHelp from './AccountPageHelp';
import MobileTabBarItems from '../../../../shared/components/common/mobile-tab-bar/MobileTabBarItems';
import PlanTemplatesDialog from '../components/plan-templates/PlanTemplatesDialog';
import { MonitorType } from '../../../../shared/types/monitor/MonitorType';
import useLayoutScrollbarWidth from '../../../../shared/hooks/scroll/UseLayoutScrollbarWidth';
import { copyTextToClipboard } from '../../../../shared/utils/utils';

// Human-readable labels for the audit-log field keys (mirrors the account edit form).
const AUDIT_FIELD_LABELS: Record<string, string> = {
  email: 'Email',
  firstName: 'First name',
  lastName: 'Last name',
  accountStatus: 'Account status',
  trialDays: 'Trial days',
  maxUsers: 'Max users',
  questionLimit: 'Question limit',
  discountId: 'Discount ID',
  isCustomSettings: 'Custom settings',
  paymentSuspended: 'Payment suspended',
  hasBrandAccount: 'Brand account',
  llmQueryProvider: 'Query provider',
  maxGeoReportsPerMonth: 'Max GEO reports / month',
  maxAiVisibilityReportsPerMonth: 'Max AI Visibility reports / month',
  maxPromptAnalysesPerMonth: 'Max Prompt Analyst reviews / month',
  features: 'Features',
  addons: 'Addons',
  monitoringInterval: 'Monitoring frequency',
};

const auditFieldLabel = (field: string): string => AUDIT_FIELD_LABELS[field] ?? field;

// Each editable block saves on its own: the payload is the LOADED account with only that block's fields
// swapped for the current inputs, so an unsaved edit in another block never rides along.
type Section = 'account' | 'models' | 'addons' | 'limits';

const SECTION_LABELS: Record<Section, string> = {
  account: 'Account data',
  models: 'AI models',
  addons: 'Addons',
  limits: 'Limits & schedule',
};

type DrawerTab = 'notes' | 'admin' | 'user' | 'help';

const initialsOf = (firstName: string, lastName: string, email: string): string => {
  const letters = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.trim();
  return (letters || email.slice(0, 2)).toUpperCase();
};

// Admin audit actions — the log holds more than account edits (project reassignment, question
// deletions), so the header must follow `entry.action` rather than assume one kind of entry.
const AUDIT_ACTION_LABELS: Record<string, string> = {
  ACCOUNT_DATA_CHANGED: 'Account data changed',
  PROJECT_REASSIGNED: 'Project reassigned',
  PROJECT_UNASSIGNED: 'Project moved to another account',
  PROJECT_DELETED: 'Project deleted by admin',
  QUESTION_ADDED: 'Questions added by admin',
  QUESTION_DELETED: 'Questions deleted by admin',
  PROJECTS_SCHEDULE_SYNCED: 'Projects synced to account frequency',
  PROJECT_INDUSTRY_CATEGORY_CHANGED: 'Project industry category changed',
  ACCOUNT_BANNED: 'Account banned',
  ACCOUNT_UNBANNED: 'Account unbanned',
  ACCOUNT_BAN_EXTENDED: 'Ban extended',
};

const auditActionLabel = (action: string): string => AUDIT_ACTION_LABELS[action] ?? action;

// One-line summary for the actions that carry ids instead of a field diff.
const auditActionSummary = (entry: AdminAuditLogEntry): string => {
  const {
    projectId,
    projectName,
    newOwnerId,
    monitorIds,
    count,
    from,
    to,
    projectsPaused,
    monitorsPaused,
    reason,
    trigger,
    field,
    snippet,
    bannedUntil,
    hours,
    automatic,
  } = entry.details ?? {};
  switch (entry.action) {
    case 'ACCOUNT_BANNED': {
      const paused = `${projectsPaused ?? 0} projects and ${monitorsPaused ?? 0} prompts paused`;
      const untilText = bannedUntil
        ? `, until ${format(new Date(bannedUntil), 'yyyy-MM-dd HH:mm')}`
        : '';
      return reason === 'ABUSE'
        ? `Automatic: dangerous markup in ${trigger} (${field}): "${snippet}"${untilText} — ${paused}`
        : paused;
    }
    case 'ACCOUNT_BAN_EXTENDED':
      return `+${hours ?? 24} h, until ${
        bannedUntil ? format(new Date(bannedUntil), 'yyyy-MM-dd HH:mm') : '?'
      }`;
    case 'ACCOUNT_UNBANNED':
      return automatic ? 'Temporary ban ran out (lifted automatically)' : '';
    case 'PROJECT_INDUSTRY_CATEGORY_CHANGED': {
      const project = projectName
        ? `${projectName}${projectId ? ` (#${projectId})` : ''}`
        : `project #${projectId}`;
      return `${project}: ${from ?? 'automatic'} → ${to ?? 'automatic'}`;
    }
    case 'PROJECT_REASSIGNED':
    case 'PROJECT_UNASSIGNED': {
      const project = projectName
        ? `${projectName}${projectId ? ` (#${projectId})` : ''}`
        : projectId
        ? `project #${projectId}`
        : '';
      return [project, newOwnerId ? `→ account #${newOwnerId}` : ''].filter(Boolean).join(' ');
    }
    case 'PROJECT_DELETED':
      return projectName ? `${projectName}${projectId ? ` (#${projectId})` : ''}` : '';
    case 'QUESTION_ADDED':
    case 'QUESTION_DELETED': {
      const total = count ?? monitorIds?.length;
      const parts: string[] = [];
      if (total != null) parts.push(`${total} question${total === 1 ? '' : 's'}`);
      if (projectId) parts.push(`in project #${projectId}`);
      return parts.join(' ');
    }
    default:
      return '';
  }
};

const formatAuditValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return String(value);
};

// Human-readable labels for the client's own activity actions.
const USER_ACTIVITY_LABELS: Record<string, string> = {
  PROJECT_CREATED: 'Project created',
  PROJECT_DELETED: 'Project deleted',
  QUESTION_ADDED: 'Questions added',
  QUESTION_DELETED: 'Questions deleted',
  PASSWORD_CHANGED: 'Password changed',
  NAME_CHANGED: 'Name changed',
  PLAN_CHANGED: 'Plan changed',
  SUBSCRIPTION_ACTIVATED: 'Subscription activated',
  USER_ADDED: 'User invited',
  USER_REMOVED: 'User removed',
};

const userActivityLabel = (action: string): string => USER_ACTIVITY_LABELS[action] ?? action;

// One-line description built from the entry's `details` bag (shape varies per action).
const userActivityDescription = (entry: UserActivityLogEntry): string => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const d = (entry.details ?? {}) as any;
  switch (entry.action) {
    case 'PROJECT_CREATED':
    case 'PROJECT_DELETED':
      return d.projectName
        ? `${d.projectName}${d.projectId ? ` (#${d.projectId})` : ''}`
        : d.projectId
        ? `#${d.projectId}`
        : '';
    case 'QUESTION_ADDED':
    case 'QUESTION_DELETED': {
      const count = d.count ?? (Array.isArray(d.monitorIds) ? d.monitorIds.length : undefined);
      const parts: string[] = [];
      if (count != null) parts.push(`${count} question${count === 1 ? '' : 's'}`);
      if (d.projectId) parts.push(`in project #${d.projectId}`);
      if (d.prompt) parts.push(`"${d.prompt}"`);
      return parts.join(' ');
    }
    case 'NAME_CHANGED': {
      const from = d.from ? `${d.from.firstName ?? ''} ${d.from.lastName ?? ''}`.trim() : '';
      const to = d.to ? `${d.to.firstName ?? ''} ${d.to.lastName ?? ''}`.trim() : '';
      return `${from || '—'} → ${to || '—'}`;
    }
    // `from` mają tylko starsze wpisy i zawsze równa się `to` (błąd źródła) — czytamy sam plan docelowy.
    case 'PLAN_CHANGED':
      return String(d.to ?? d.from ?? '—');
    case 'SUBSCRIPTION_ACTIVATED': {
      const parts: string[] = [];
      if (d.accountType) parts.push(String(d.accountType));
      if (d.price != null) parts.push(`${d.price}${d.currency ? ` ${d.currency}` : ''}`);
      if (d.subscriptionId) parts.push(`(${d.subscriptionId})`);
      return parts.join(' · ');
    }
    case 'USER_ADDED':
      return d.email ? `${d.email}${d.role ? ` (${d.role})` : ''}` : '';
    case 'USER_REMOVED':
      return d.email ? String(d.email) : d.removedUserId ? `#${d.removedUserId}` : '';
    default:
      return '';
  }
};

const AdminUsersEditPageV2: React.FC<{ onSwitchLayout: () => void }> = ({ onSwitchLayout }) => {
  const { t } = useTranslation();
  // Edge tabs sit next to the page scrollbar, never on it.
  const sideTabsOffset = useLayoutScrollbarWidth();
  const { id } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { accountResponse, refetchData } = useAccount();
  // Read-only admins can browse the account but not mutate it (backend enforces this too).
  const canEdit = getAdminPermissions(accountResponse)[AdminResource.ACCOUNTS].write;
  const canSeeCosts = getAdminPermissions(accountResponse)[AdminResource.COSTS].read;
  // Templates: `read` shows the picker (applying still needs ACCOUNTS write), `write` unlocks the manager.
  const templatePermission = getAdminPermissions(accountResponse)[AdminResource.PLAN_TEMPLATES];
  const [accountCosts, setAccountCosts] = useState<AdminAccountCostResponse | null>(null);
  const [invitedUser, setInvitedUser] = useState<InvitedUserResponse[] | null>(null);
  const [userData, setUserData] = useState<AccountDataResponse | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [range, setRange] = useState<Date[] | null>(null);
  // `daysBetween` is re-derived from `created` + the picked end date, so with a DST shift inside the trial
  // (or trialDays = 0) it never equals the stored value exactly — the block counts as dirty only once the
  // admin actually touched the date picker.
  const [rangeTouched, setRangeTouched] = useState<boolean>(false);
  const [accountStatus, setAccountStatus] = useState<AccountStatus | null>(null);
  // Mirrors resolveAiVisibilityMonthlyLimit in llm-common — keep the numbers in sync.
  const aiVisibilityDefaultLimit = accountStatus === 'TRIAL' ? 2 : 5;
  const [accountType, setAccountType] = useState<AccountType | null>(null);
  const [isCustomSettings, setCustomSettings] = useState<boolean>(false);
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [paymentSuspended, setPaymentSuspended] = useState<boolean>(false);
  const [hasBrandAccount, setHasBrandAccount] = useState<boolean>(false);
  const [isSemrushProvider, setSemrushProvider] = useState<boolean>(false);
  const [monitoringPreset, setMonitoringPreset] = useState<MonitoringSchedulePreset>('DAILY');
  const [projectsOffSchedule, setProjectsOffSchedule] = useState<number>(0);
  const [syncingSchedule, setSyncingSchedule] = useState<boolean>(false);
  const [apiKeys, setApiKeys] = useState<ApiKeyResponse[] | null>(null);
  // Side drawer (notes + both logs). The logs load on the first open, not with the page.
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [drawerTab, setDrawerTab] = useState<DrawerTab>('notes');
  const [savingSection, setSavingSection] = useState<Section | null>(null);
  const [planTemplates, setPlanTemplates] = useState<AdminPlanTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState<boolean>(false);
  const [errorSection, setErrorSection] = useState<Section | null>(null);
  const [auditLog, setAuditLog] = useState<AdminAuditLogEntry[] | null>(null);
  const [userActivity, setUserActivity] = useState<UserActivityLogEntry[] | null>(null);
  const { showToast, toastElement } = useToast();
  const [projectsForId, setProjectsForId] = useState<AdminAccountProject[]>([]);
  const [adminNotes, setAdminNotes] = useState<AdminNoteResponse[]>([]);
  const [newNoteContent, setNewNoteContent] = useState<string>('');
  const [isAddingNote, setIsAddingNote] = useState<boolean>(false);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingContent, setEditingContent] = useState<string>('');
  const [isSavingEdit, setIsSavingEdit] = useState<boolean>(false);
  const [showAllNotes, setShowAllNotes] = useState<boolean>(false);
  const [modelFilter, setModelFilter] = useState<string>('');
  const [addonFilter, setAddonFilter] = useState<string>('');
  const NOTES_INITIAL_VISIBLE = 5;
  const {
    register,
    getValues,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useForm();
  let confirmRef: ConfirmDialogReturn;
  const calendarRef = useRef<Calendar>(null);
  const accountStatusOpt: { name: string; type: AccountStatus }[] = [
    { name: 'Paid', type: 'PAID' },
    { name: 'Trial', type: 'TRIAL' },
    { name: 'Overdue', type: 'OVERDUE' },
    { name: 'Blocked', type: 'BLOCKED' },
    { name: 'Disabled', type: 'DISABLED' },
  ];
  const accountTypeOpt: { name: string; type: AccountType }[] = [
    { name: 'Trial', type: 'TRIAL' },
    { name: 'Standard', type: 'STANDARD' },
    { name: 'Pro', type: 'PRO' },
    { name: 'Advanced', type: 'ADVANCED' },
    { name: 'Enterprise', type: 'ENTERPRISE' },
  ];
  const monitoringScheduleOpt: { name: string; preset: MonitoringSchedulePreset }[] = [
    { name: 'Daily', preset: 'DAILY' },
    { name: 'Weekly', preset: 'WEEKLY' },
  ];
  // The button syncs to the SAVED frequency and says so in its label, so an unsaved dropdown change
  // does not block it; only "nothing differs" does.
  const savedScheduleName =
    monitoringScheduleOpt.find((o) => o.preset === presetOfInterval(userData?.monitoringInterval))
      ?.name ?? 'Daily';
  // The label carries the count of projects on a different frequency; 0 disables the button.
  const canSyncSchedule = canEdit && projectsOffSchedule > 0 && !syncingSchedule;
  const syncScheduleTooltip = projectsOffSchedule === 0 ? 'All projects already use this frequency' : '';

  const syncProjectsSchedule = async () => {
    setSyncingSchedule(true);
    try {
      const { projectsSynced } = await syncMonitoringSchedule(Number(id));
      setProjectsOffSchedule(0);
      showToast(`Synced ${projectsSynced} project(s) to the account frequency`);
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Failed to sync projects', 'error');
    } finally {
      setSyncingSchedule(false);
    }
  };

  const clearApiError = () => {
    setApiError(null);
  };

  const hideDialog = () => {
    if (confirmRef) {
      confirmRef.hide();
    }

    window.location.reload();
  };
  const inviteUser = () => {
    confirmRef = confirmDialog({
      message: <AdminInviteUser accountId={Number(id)} afterSave={hideDialog} />,
      header: 'Invite user',
      defaultFocus: 'accept',
      acceptClassName: 'confirm-change-name-btn',
      footer: () => null,
    });
  };

  useEffect(() => {
    if (!canSeeCosts) return;
    getAccountCosts(Number(id))
      .then(setAccountCosts)
      .catch(() => setAccountCosts(null));
  }, [id, canSeeCosts]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [accountResponse, invitedUserResponse, apiKeysResponse, projects] = await Promise.all([
          getAccountData(Number(id)),
          getInvitedUsers(Number(id)),
          getAccountApiKeys(Number(id)),
          getProjectsForAccount(Number(id)),
        ]);

        setProjectsForId(projects);

        setUserData(accountResponse);
        setInvitedUser(invitedUserResponse);
        setApiKeys(apiKeysResponse);
        setAccountStatus(accountResponse.accountStatus);
        setAccountType(accountResponse.accountType);
        setCustomSettings(accountResponse.isCustomSettings);
        setPaymentSuspended(accountResponse.paymentSuspended || false);
        setHasBrandAccount(accountResponse.hasBrandAccount || false);
        setSemrushProvider(accountResponse.llmQueryProvider === 'SEMRUSH');
        setSelectedAddons(accountResponse.addons || []);
        setSelectedFeatures(accountResponse.features || []);
        setMonitoringPreset(presetOfInterval(accountResponse.monitoringInterval));
        setProjectsOffSchedule(accountResponse.projectsOffScheduleCount ?? 0);
        setAdminNotes(accountResponse.adminNotes ?? []);

        const startDate = new Date(accountResponse.created);
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + accountResponse.trialDays);
        setRange([startDate, endDate]);
      } catch (error) {
        console.error('Failed to load account data:', error);
      }
    };

    loadData();
  }, [id]);

  const openDrawer = (tab: DrawerTab) => {
    setDrawerTab(tab);
    setDrawerOpen(true);
  };

  // Separate from the main load: the page renders without the logs, the "Recent activity" block fills in.
  useEffect(() => {
    getAuditLog(Number(id)).then(setAuditLog);
    getUserActivityLog(Number(id)).then(setUserActivity);
  }, [id]);

  useEffect(() => {
    if (templatePermission.read) getPlanTemplates().then(setPlanTemplates);
  }, [templatePermission.read]);

  const copyToClipboard = async (text: string) => {
    const copied = await copyTextToClipboard(text);
    showToast(copied ? t('copied') : t('copyFailed'), copied ? 'success' : 'error');
  };

  const loadAccountApiKeys = async () => {
    try {
      const apiKeysResponse = await getAccountApiKeys(Number(id));
      setApiKeys(apiKeysResponse);
    } catch (error) {
      console.error('Failed to load API keys for account:', error);
    }
  };

  const getKeyStatus = (expires: Date | null | undefined) => {
    if (!expires) {
      return { status: 'active', label: t('neverExpires') };
    }

    const now = new Date();
    const expiresDate = new Date(expires);
    const daysUntilExpiry = Math.ceil((expiresDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (now > expiresDate) {
      return { status: 'expired', label: t('expired') };
    } else if (daysUntilExpiry <= 7) {
      return { status: 'expires-soon', label: t('expiresInDays', { days: daysUntilExpiry }) };
    } else {
      return { status: 'active', label: t('expiresInDays', { days: daysUntilExpiry }) };
    }
  };

  const addNewAccountApiKey = () => {
    confirmRef = confirmDialog({
      message: (
        <AdminApiKeyCreateForm
          accountId={Number(id)}
          afterSave={async () => {
            if (confirmRef) {
              confirmRef.hide();
            }
            await loadAccountApiKeys();
          }}
        />
      ),
      header: t('createApiKey'),
      defaultFocus: 'accept',
      acceptClassName: 'confirm-change-name-btn',
      footer: () => null,
    });
  };

  const AdminApiKeyCreateForm: React.FC<{ accountId: number; afterSave: () => void }> = ({
    accountId,
    afterSave,
  }) => {
    const [localApiError, setLocalApiError] = useState<string | null>(null);
    const {
      register: registerApiKey,
      handleSubmit: handleSubmitApiKey,
      formState: { errors: apiKeyErrors },
    } = useForm();

    const validityOptions = [
      { label: t('never'), value: undefined },
      { label: t('30Days'), value: 30 },
      { label: t('90Days'), value: 90 },
      { label: t('180Days'), value: 180 },
      { label: t('365Days'), value: 365 },
    ];

    const onSubmitApiKey = async (formData: any) => {
      try {
        setLocalApiError(null);
        const request: CreateApiKeyRequest = {
          validForDays: formData.validForDays ? parseInt(formData.validForDays, 10) : undefined,
          description: formData.description || undefined,
        };
        await createAccountApiKey(accountId, request);
        afterSave();
      } catch (error: any) {
        console.error('Failed to create API key for account:', error);
        setLocalApiError(error.response?.data?.error || t('createApiKeyError'));
      }
    };

    const clearLocalApiError = () => {
      setLocalApiError(null);
    };

    return (
      <div>
        <form onSubmit={handleSubmitApiKey(onSubmitApiKey)} className="form-box__form">
          <div className="form-box__row">
            <div className="form-box__label">{t('description')}:</div>
            <InputText
              onInput={clearLocalApiError}
              {...registerApiKey('description')}
              placeholder={t('enterDescription')}
              className={apiKeyErrors.description || localApiError ? 'form-box__invalid' : ''}
            />
            {apiKeyErrors.description && <span>{apiKeyErrors.description.message as string}</span>}
          </div>

          <div className="form-box__row">
            <div className="form-box__label">{t('validityPeriod')}:</div>
            <Dropdown
              {...registerApiKey('validForDays')}
              options={validityOptions}
              placeholder={t('selectValidityPeriod')}
              className="dropdown"
              optionLabel="label"
              optionValue="value"
            />
          </div>

          {localApiError && <div className="p-error">{localApiError}</div>}

          <Button className="btn btn--sm" label={t('create')} type="submit" />
        </form>
      </div>
    );
  };

  const handleAddNote = async () => {
    if (!id) return;
    const trimmed = newNoteContent.trim();
    if (trimmed.length === 0) return;
    setIsAddingNote(true);
    try {
      const created = await addAdminNote(Number(id), trimmed);
      setAdminNotes((prev) => [created, ...prev]);
      setNewNoteContent('');
    } catch (error) {
      console.error('Failed to add admin note', error);
    } finally {
      setIsAddingNote(false);
    }
  };

  const handleStartEditNote = (note: AdminNoteResponse) => {
    setEditingNoteId(note.id);
    setEditingContent(note.content);
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditingContent('');
  };

  const handleSaveEditNote = async () => {
    if (!id || editingNoteId == null) return;
    const trimmed = editingContent.trim();
    if (trimmed.length === 0) return;
    setIsSavingEdit(true);
    try {
      const updated = await updateAdminNote(Number(id), editingNoteId, trimmed);
      setAdminNotes((prev) => prev.map((n) => (n.id === editingNoteId ? updated : n)));
      setEditingNoteId(null);
      setEditingContent('');
    } catch (error) {
      console.error('Failed to update admin note', error);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!id) return;
    // Snapshot the row by id, not the whole array — if another note is added
    // mid-flight, a wholesale array rollback would drop it.
    const snapshot = adminNotes.find((n) => n.id === noteId);
    setAdminNotes((prev) => prev.filter((n) => n.id !== noteId));
    try {
      await deleteAdminNote(Number(id), noteId);
    } catch (error) {
      console.error('Failed to delete admin note', error);
      if (snapshot) {
        setAdminNotes((curr) => (curr.some((n) => n.id === noteId) ? curr : [...curr, snapshot]));
      }
    }
  };

  const handleOpenPaddle = () => {
    if (!userData?.subscriptionId) return;
    window.open(
      `https://vendors.paddle.com/subscriptions-v2/${encodeURIComponent(userData.subscriptionId)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleOpenIntercom = () => {
    if (!userData?.intercomUrl) return;
    window.open(userData.intercomUrl, '_blank', 'noopener,noreferrer');
  };

  const isBanned = Boolean(userData?.bannedAt);

  const handleExtendBan = () => {
    if (!id) return;
    confirmDialog({
      message:
        'Extend the temporary ban by 24 hours? The account stays blocked and its projects paused until the new end.',
      header: 'Extend ban',
      defaultFocus: 'accept',
      acceptLabel: 'Extend 24 h',
      rejectLabel: t('cancel'),
      className: 'dialog-box',
      accept: async () => {
        try {
          await extendBan(Number(id));
          window.location.reload();
        } catch (error) {
          console.error('Failed to extend ban', error);
        }
      },
    });
  };

  const handleToggleBan = () => {
    if (!id) return;
    confirmDialog({
      message: isBanned
        ? 'Lift the ban? The prompts paused by the ban will resume monitoring.'
        : 'Ban this account? The user will only see a suspension screen and every project and prompt will be paused.',
      header: isBanned ? 'Unban account' : 'Ban account',
      defaultFocus: 'reject',
      acceptLabel: isBanned ? 'Unban' : 'Ban',
      rejectLabel: t('cancel'),
      className: 'dialog-box',
      accept: async () => {
        try {
          await (isBanned ? unbanAccount(Number(id)) : banAccount(Number(id)));
          window.location.reload();
        } catch (error) {
          console.error('Failed to change ban state', error);
        }
      },
    });
  };

  const handleImpersonate = async () => {
    if (!id) return;
    try {
      const originalTokenData = getTokenData();
      if (originalTokenData) {
        sessionStorage.setItem(IMPERSONATION_ORIGINAL_TOKEN_KEY, JSON.stringify(originalTokenData));
        sessionStorage.setItem(IMPERSONATION_TARGET_ID_KEY, id);
      }
      const tokenData = await impersonateUser(Number(id));
      login(tokenData);
      refetchData();
      navigate(PROJECTS);
    } catch (error) {
      console.error('Failed to impersonate user', error);
    }
  };

  if (!projectsForId || !userData || invitedUser === null || !range || !apiKeys) {
    return (
      <div className="admin-edit-user">
        <Loader />
      </div>
    );
  }

  const daysBetween =
    range[0] && range[1]
      ? Math.max(1, Math.ceil((range[1].getTime() - range[0].getTime()) / (1000 * 60 * 60 * 24)))
      : 0;

  const isUnlimitedPrompts = (userData.accountType as string) === 'PAID';
  const promptUsagePercent =
    isUnlimitedPrompts || !userData.maxMonitorCount
      ? 0
      : Math.round((userData.monitorCount / userData.maxMonitorCount) * 100);
  const promptUsageModifier =
    promptUsagePercent >= 100 ? 'over' : promptUsagePercent >= 80 ? 'high' : 'ok';
  const statusLabel =
    accountStatusOpt.find((o) => o.type === userData.accountStatus)?.name ?? userData.accountStatus;

  const matchesFilter = (filter: string, ...texts: (string | undefined)[]) => {
    const needle = filter.trim().toLowerCase();
    return !needle || texts.some((text) => text?.toLowerCase().includes(needle));
  };
  const visibleModels = allMonitorTypes.filter((model) =>
    matchesFilter(
      modelFilter,
      model,
      MODEL_CATALOG[model]?.provider,
      getMonitorName(model as MonitorType)
    )
  );
  const visibleAddons = allAddons.filter((addon) =>
    matchesFilter(addonFilter, addon, addonDescriptions[addon])
  );

  const buildPayload = (section: Section): UserAccountData => {
    const form = getValues();
    const loaded = {
      accountId: Number(id),
      email: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName,
      discountId: userData.discountId ?? '',
      accountStatus: userData.accountStatus,
      accountType: userData.accountType,
      isCustomSettings: userData.isCustomSettings,
      paymentSuspended: userData.paymentSuspended || false,
      hasBrandAccount: userData.hasBrandAccount || false,
      llmQueryProvider: userData.llmQueryProvider ?? null,
      features: userData.features ?? [],
      addons: userData.addons ?? [],
      questionLimit: userData.maxMonitorCount,
      maxUsers: userData.maxUsers,
      trialDays: userData.trialDays,
      maxGeoReportsPerMonth: userData.maxGeoReportsPerMonth,
      maxAiVisibilityReportsPerMonth: userData.maxAiVisibilityReportsPerMonth,
      maxPromptAnalysesPerMonth: userData.maxPromptAnalysesPerMonth,
      monitoringInterval: userData.monitoringInterval,
    };
    switch (section) {
      case 'account':
        return {
          ...loaded,
          email: form.email ?? userData.email,
          firstName: form.firstName ?? userData.firstName,
          lastName: form.lastName ?? userData.lastName,
          discountId: form.discountId ?? userData.discountId ?? '',
          accountStatus,
          accountType,
          isCustomSettings,
          paymentSuspended,
          hasBrandAccount,
          llmQueryProvider: isSemrushProvider ? 'SEMRUSH' : null,
        } as UserAccountData;
      case 'models':
        return { ...loaded, features: selectedFeatures } as UserAccountData;
      case 'addons':
        return { ...loaded, addons: selectedAddons } as UserAccountData;
      case 'limits':
        return {
          ...loaded,
          questionLimit: Number(form.questionLimit),
          maxUsers: Number(form.maxUsers),
          trialDays: Number(daysBetween),
          maxGeoReportsPerMonth: form.maxGeoReportsPerMonth ? Number(form.maxGeoReportsPerMonth) : null,
          // '' -> null (use the default). '0' must survive as 0 (blocked), so no truthiness check here.
          maxAiVisibilityReportsPerMonth:
            String(form.maxAiVisibilityReportsPerMonth ?? '').trim() === ''
              ? null
              : Number(form.maxAiVisibilityReportsPerMonth),
          maxPromptAnalysesPerMonth:
            String(form.maxPromptAnalysesPerMonth ?? '').trim() === ''
              ? null
              : Number(form.maxPromptAnalysesPerMonth),
          monitoringInterval: MONITORING_SCHEDULE_PRESETS[monitoringPreset],
        } as UserAccountData;
    }
  };

  // After a save the account is re-read (no page reload — edits pending in OTHER blocks must survive) and
  // the saved block's local state is re-synced from the server, which may normalise it (implied addons).
  const saveSection = async (section: Section) => {
    if (section === 'account' && !(await trigger(['email']))) return;
    const payload = buildPayload(section);
    setSavingSection(section);
    setApiError(null);
    setErrorSection(null);
    try {
      await changeUserAccountData(payload);
      const fresh = await getAccountData(Number(id));
      if (fresh && 'email' in fresh) {
        setUserData(fresh);
        setProjectsOffSchedule(fresh.projectsOffScheduleCount ?? 0);
        if (section === 'addons') setSelectedAddons(fresh.addons ?? []);
        if (section === 'models') setSelectedFeatures(fresh.features ?? []);
        if (section === 'account') {
          setAccountStatus(fresh.accountStatus);
          setAccountType(fresh.accountType);
        }
        if (section === 'limits') {
          setMonitoringPreset(presetOfInterval(fresh.monitoringInterval));
          setRangeTouched(false);
        }
      }
      showToast(`${SECTION_LABELS[section]} saved`);
    } catch (err: any) {
      if (err?.statusCode === 401) return;
      const message = err?.response?.data?.error || 'Failed to save';
      setErrorSection(section);
      setApiError(message);
      showToast(message, 'error');
    } finally {
      setSavingSection(null);
    }
  };

  // Templates replace BOTH lists in one save (one audit entry), then the lists re-sync from the server.
  const savePlan = async (features: string[], addons: string[]) => {
    setSavingSection('models');
    setApiError(null);
    setErrorSection(null);
    try {
      await changeUserAccountData({ ...buildPayload('models'), features, addons } as UserAccountData);
      const fresh = await getAccountData(Number(id));
      if (fresh && 'email' in fresh) {
        setUserData(fresh);
        setSelectedFeatures(fresh.features ?? []);
        setSelectedAddons(fresh.addons ?? []);
      }
      showToast('Models and addons saved');
    } catch (err: any) {
      if (err?.statusCode === 401) return;
      const message = err?.response?.data?.error || 'Failed to save';
      setErrorSection('models');
      setApiError(message);
      showToast(message, 'error');
    } finally {
      setSavingSection(null);
    }
  };

  const selectedTemplate = planTemplates.find((template) => template.id === selectedTemplateId) ?? null;
  const templateSummary = (template: AdminPlanTemplate) =>
    `${template.name} · ${template.features.length} models, ${template.addons.length} addons`;

  const applyTemplate = () => {
    if (!selectedTemplate) return;
    confirmDialog({
      message: `Apply "${selectedTemplate.name}"? The account's models and addons will be replaced with the template's and saved.`,
      header: 'Apply plan template',
      defaultFocus: 'accept',
      acceptLabel: 'Apply & save',
      rejectLabel: t('cancel'),
      className: 'dialog-box',
      accept: () => savePlan(selectedTemplate.features, selectedTemplate.addons),
    });
  };

  // Templates row above the models/addons blocks: pick → apply (replaces both lists and saves), or
  // save / overwrite a template from the lists as ticked now.
  const renderPlanTemplates = () => (
    <div className="admin-edit-user__block">
      <div className="admin-edit-user__card-head">
        <div className="admin-edit-user__card-title">Plan templates</div>
        {templatePermission.write && (
          <Button
            type="button"
            className="btn btn--sm btn--secondary"
            onClick={() => setTemplatesDialogOpen(true)}
          >
            <span className="ico--settings ico--xs" />
            Manage templates
          </Button>
        )}
      </div>
      <div className="admin-edit-user__card admin-edit-user__templates">
        <div className="admin-edit-user__templates-row">
          <Dropdown
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.value ?? null)}
            options={planTemplates.map((template) => ({
              label: templateSummary(template),
              value: template.id,
            }))}
            placeholder={
              planTemplates.length
                ? 'Pick a template…'
                : 'No templates yet — create one under Manage templates'
            }
            className="dropdown"
            showClear
            itemTemplate={(option) => <DropdownItem name={option.label} />}
          />
          {canEdit && (
            <Button
              type="button"
              className="btn btn--sm"
              disabled={!selectedTemplate || savingSection !== null}
              onClick={applyTemplate}
            >
              <span className="ico--play ico--xs" />
              Apply & save
            </Button>
          )}
        </div>
        {selectedTemplate && (
          <div className="admin-edit-user__templates-preview">
            <span>
              <strong>Models:</strong> {selectedTemplate.features.join(', ') || '—'}
            </span>
            <span>
              <strong>Addons:</strong> {selectedTemplate.addons.join(', ') || '—'}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  // What differs from the loaded account, by field label — feeds each block's save row. Text inputs come
  // from the form (`watch`; undefined until the input mounts = unchanged), the rest from local state.
  const watched = watch();
  const textChanged = (name: string, initial: unknown): boolean =>
    watched[name] !== undefined && String(watched[name] ?? '') !== String(initial ?? '');
  const listDiff = (current: string[], initial: string[]): string => {
    const added = current.filter((v) => !initial.includes(v)).length;
    const removed = initial.filter((v) => !current.includes(v)).length;
    return [added ? `+${added}` : '', removed ? `−${removed}` : ''].filter(Boolean).join(' ');
  };
  const addonsDiff = listDiff(selectedAddons, userData.addons ?? []);
  const featuresDiff = listDiff(selectedFeatures, userData.features ?? []);
  const onlyLabels = (items: (string | false)[]): string[] =>
    items.filter((label): label is string => Boolean(label));
  const sectionChanges: Record<Section, string[]> = {
    account: onlyLabels([
      textChanged('email', userData.email) && AUDIT_FIELD_LABELS.email,
      textChanged('firstName', userData.firstName) && AUDIT_FIELD_LABELS.firstName,
      textChanged('lastName', userData.lastName) && AUDIT_FIELD_LABELS.lastName,
      textChanged('discountId', userData.discountId) && AUDIT_FIELD_LABELS.discountId,
      accountStatus !== userData.accountStatus && AUDIT_FIELD_LABELS.accountStatus,
      hasBrandAccount !== (userData.hasBrandAccount || false) && AUDIT_FIELD_LABELS.hasBrandAccount,
      isSemrushProvider !== (userData.llmQueryProvider === 'SEMRUSH') &&
        AUDIT_FIELD_LABELS.llmQueryProvider,
      isCustomSettings !== userData.isCustomSettings && AUDIT_FIELD_LABELS.isCustomSettings,
      paymentSuspended !== (userData.paymentSuspended || false) && AUDIT_FIELD_LABELS.paymentSuspended,
    ]),
    models: onlyLabels([featuresDiff !== '' && `${AUDIT_FIELD_LABELS.features} (${featuresDiff})`]),
    addons: onlyLabels([addonsDiff !== '' && `${AUDIT_FIELD_LABELS.addons} (${addonsDiff})`]),
    limits: onlyLabels([
      textChanged('questionLimit', userData.maxMonitorCount) && AUDIT_FIELD_LABELS.questionLimit,
      textChanged('maxUsers', userData.maxUsers) && AUDIT_FIELD_LABELS.maxUsers,
      rangeTouched && daysBetween !== userData.trialDays && AUDIT_FIELD_LABELS.trialDays,
      textChanged('maxGeoReportsPerMonth', userData.maxGeoReportsPerMonth) &&
        AUDIT_FIELD_LABELS.maxGeoReportsPerMonth,
      textChanged('maxAiVisibilityReportsPerMonth', userData.maxAiVisibilityReportsPerMonth) &&
        AUDIT_FIELD_LABELS.maxAiVisibilityReportsPerMonth,
      textChanged('maxPromptAnalysesPerMonth', userData.maxPromptAnalysesPerMonth) &&
        AUDIT_FIELD_LABELS.maxPromptAnalysesPerMonth,
      monitoringPreset !== presetOfInterval(userData.monitoringInterval) &&
        AUDIT_FIELD_LABELS.monitoringInterval,
    ]),
  };

  // Asks before writing: the dialog names the fields that will change.
  const confirmSave = (section: Section) => {
    const changes = sectionChanges[section];
    if (changes.length === 0) {
      void saveSection(section);
      return;
    }
    confirmDialog({
      message: `Save ${SECTION_LABELS[section]}? This changes: ${changes.join(', ')}.`,
      header: 'Save changes',
      defaultFocus: 'accept',
      acceptLabel: 'Save',
      rejectLabel: t('cancel'),
      className: 'dialog-box',
      accept: () => void saveSection(section),
    });
  };

  // Save row at the bottom of an editable block: names what differs, turns blue when something does.
  // Live validation per block (same rules the save applies). A server error after a save counts for the
  // block it came from. The block with an error gets a red frame and no Save.
  const isWholeNumber = (value: unknown) => /^\d+$/.test(String(value ?? '').trim());
  const isBlankOrWholeNumber = (value: unknown) =>
    String(value ?? '').trim() === '' || isWholeNumber(value);
  const sectionErrors: Record<Section, string | null> = {
    account:
      watched.email !== undefined && String(watched.email).trim() === '' ? 'Email is required.' : null,
    models: null,
    addons: null,
    limits:
      (watched.questionLimit !== undefined && !isWholeNumber(watched.questionLimit)
        ? 'Question limit must be a whole number.'
        : null) ??
      (watched.maxUsers !== undefined && !isWholeNumber(watched.maxUsers)
        ? 'User limit must be a whole number.'
        : null) ??
      (!isBlankOrWholeNumber(watched.maxGeoReportsPerMonth)
        ? 'GEO reports / month must be a whole number or empty.'
        : null) ??
      (!isBlankOrWholeNumber(watched.maxAiVisibilityReportsPerMonth)
        ? 'AI Visibility reports / month must be a whole number or empty.'
        : null) ??
      (!isBlankOrWholeNumber(watched.maxPromptAnalysesPerMonth)
        ? 'Prompt Analyst reviews / month must be a whole number or empty.'
        : null) ??
      (isNaN(daysBetween) ? 'Pick a trial end date.' : null),
  };
  const sectionError = (section: Section): string | null =>
    sectionErrors[section] ?? (errorSection === section ? apiError : null);

  const fieldsetClass = (section: Section) =>
    `admin-edit-user__card admin-edit-user__form-fieldset${
      sectionError(section)
        ? ' admin-edit-user__card--error'
        : sectionChanges[section].length > 0
        ? ' admin-edit-user__card--dirty'
        : ''
    }`;

  // "Cancel": back to the loaded account for that block only — form inputs through `setValue` (they are
  // uncontrolled), the rest through local state. Other blocks keep their pending edits.
  const resetSection = (section: Section) => {
    switch (section) {
      case 'account':
        setValue('email', userData.email);
        setValue('firstName', userData.firstName);
        setValue('lastName', userData.lastName);
        setValue('discountId', userData.discountId ?? '');
        setAccountStatus(userData.accountStatus);
        setAccountType(userData.accountType);
        setHasBrandAccount(userData.hasBrandAccount || false);
        setSemrushProvider(userData.llmQueryProvider === 'SEMRUSH');
        setCustomSettings(userData.isCustomSettings);
        setPaymentSuspended(userData.paymentSuspended || false);
        break;
      case 'models':
        setSelectedFeatures(userData.features ?? []);
        break;
      case 'addons':
        setSelectedAddons(userData.addons ?? []);
        break;
      case 'limits': {
        setValue('questionLimit', String(userData.maxMonitorCount));
        setValue('maxUsers', String(userData.maxUsers));
        setValue('maxGeoReportsPerMonth', String(userData.maxGeoReportsPerMonth ?? ''));
        setValue(
          'maxAiVisibilityReportsPerMonth',
          String(userData.maxAiVisibilityReportsPerMonth ?? '')
        );
        setValue('maxPromptAnalysesPerMonth', String(userData.maxPromptAnalysesPerMonth ?? ''));
        const startDate = new Date(userData.created);
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + userData.trialDays);
        setRange([startDate, endDate]);
        setRangeTouched(false);
        setMonitoringPreset(presetOfInterval(userData.monitoringInterval));
        break;
      }
    }
    if (errorSection === section) {
      setErrorSection(null);
      setApiError(null);
    }
  };

  const renderSectionFooter = (section: Section) => {
    if (!canEdit) return null;
    const changes = sectionChanges[section];
    const dirty = changes.length > 0;
    const error = sectionError(section);
    return (
      <div className="admin-edit-user__card-footer">
        <span className="admin-edit-user__save-hint">
          {error ? (
            <>
              <span className="ico ico--sm ico--warning admin-edit-user__save-icon admin-edit-user__save-icon--error" />
              <span className="admin-edit-user__field-error">{error}</span>
            </>
          ) : dirty ? (
            <>
              <span className="ico ico--sm ico--warning admin-edit-user__save-icon" />
              Unsaved: <strong>{changes.join(', ')}</strong>
            </>
          ) : (
            <>
              <span className="ico ico--sm ico--circle-check admin-edit-user__save-icon admin-edit-user__save-icon--ok" />
              Everything saved
            </>
          )}
        </span>
        {dirty && (
          <Button
            type="button"
            className="btn btn--sm admin-edit-user__cancel-btn"
            title="Discard the changes in this block"
            disabled={savingSection !== null}
            onClick={() => resetSection(section)}
          >
            <span className="ico--close ico--xs" />
            Cancel
          </Button>
        )}
        {dirty && (
          <Button
            type="button"
            className="btn btn--sm admin-edit-user__save-btn--dirty"
            disabled={savingSection !== null || error !== null}
            onClick={() => confirmSave(section)}
          >
            <span className="ico--tick ico--xs" />
            {savingSection === section ? 'Saving…' : 'Save'}
          </Button>
        )}
      </div>
    );
  };

  const renderField = (
    label: string,
    control: React.ReactNode,
    hint?: React.ReactNode,
    error?: string
  ) => (
    <div className="admin-edit-user__field">
      <div className="form-box__label">{label}</div>
      {control}
      {error && <span className="admin-edit-user__field-error">{error}</span>}
      {hint && <small className="form-box__hint">{hint}</small>}
    </div>
  );

  const renderKv = (label: string, value: React.ReactNode) => (
    <div className="admin-edit-user__kv-row">
      <span className="admin-edit-user__kv-label">{label}</span>
      <span className="admin-edit-user__kv-value">{value}</span>
    </div>
  );

  // The client's own activity log — rendered beside the admin "Activity log" at the bottom of the page.
  const renderUserActivity = () => (
    <div className="admin-edit-user__history-body">
      {userActivity === null ? (
        <Loader />
      ) : (
        <>
          {userActivity.length === 0 ? (
            <div className="admin-edit-user__audit-empty">No activity recorded yet.</div>
          ) : (
            <ul className="admin-edit-user__audit-list">
              {userActivity.map((entry) => {
                const description = userActivityDescription(entry);
                return (
                  <li key={entry.id} className="admin-edit-user__audit-item">
                    <div className="admin-edit-user__audit-head">
                      <span className="admin-edit-user__audit-action">
                        {userActivityLabel(entry.action)}
                        {entry.isAdminAction && (
                          <span className="admin-edit-user__audit-badge">admin</span>
                        )}
                      </span>
                      <span className="admin-edit-user__audit-date">
                        {format(new Date(entry.created), 'yyyy-MM-dd HH:mm')}
                      </span>
                    </div>
                    <div className="admin-edit-user__audit-actor">
                      by {entry.actorEmail || `user #${entry.actorUserId}`}
                      {entry.isAdminAction && ' (superadmin, not the client)'}
                    </div>
                    {description && (
                      <ul className="admin-edit-user__audit-changes">
                        <li className="admin-edit-user__audit-change">{description}</li>
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );

  const renderAuditLog = () => (
    <div className="admin-edit-user__history-body">
      {auditLog === null ? (
        <Loader />
      ) : (
        <>
          {auditLog.length === 0 ? (
            <div className="admin-edit-user__audit-empty">No changes recorded yet.</div>
          ) : (
            <ul className="admin-edit-user__audit-list">
              {auditLog.map((entry) => {
                const changes = entry.details?.changes ?? [];
                const summary = auditActionSummary(entry);
                return (
                  <li key={entry.id} className="admin-edit-user__audit-item">
                    <div className="admin-edit-user__audit-head">
                      <span className="admin-edit-user__audit-action">
                        {auditActionLabel(entry.action)}
                      </span>
                      <span className="admin-edit-user__audit-date">
                        {format(new Date(entry.created), 'yyyy-MM-dd HH:mm')}
                      </span>
                    </div>
                    <div className="admin-edit-user__audit-actor">
                      by {entry.actorEmail || `user #${entry.actorUserId}`}
                    </div>
                    <ul className="admin-edit-user__audit-changes">
                      {summary && <li className="admin-edit-user__audit-change">{summary}</li>}
                      {changes.map((change: AccountFieldChange, i: number) => (
                        <li key={i} className="admin-edit-user__audit-change">
                          <span className="admin-edit-user__audit-field">
                            {auditFieldLabel(change.field)}:
                          </span>{' '}
                          {change.added || change.removed ? (
                            <span className="admin-edit-user__audit-diff">
                              {(change.added ?? []).map((v) => (
                                <span key={`a-${v}`} className="admin-edit-user__audit-added">
                                  +{v}
                                </span>
                              ))}
                              {(change.removed ?? []).map((v) => (
                                <span key={`r-${v}`} className="admin-edit-user__audit-removed">
                                  −{v}
                                </span>
                              ))}
                            </span>
                          ) : (
                            <span className="admin-edit-user__audit-diff">
                              <span className="admin-edit-user__audit-from">
                                {formatAuditValue(change.from)}
                              </span>
                              <span className="admin-edit-user__audit-arrow">→</span>
                              <span className="admin-edit-user__audit-to">
                                {formatAuditValue(change.to)}
                              </span>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );

  // The newest few entries of both logs, merged by date; the full lists live in the drawer.
  const renderRecentActivity = () => {
    const recent = [
      ...(auditLog ?? []).map((entry) => ({
        key: `a-${entry.id}`,
        created: entry.created,
        label: auditActionLabel(entry.action),
        detail:
          auditActionSummary(entry) ||
          (entry.details?.changes ?? [])
            .map((c: AccountFieldChange) => auditFieldLabel(c.field))
            .join(', '),
        actor: entry.actorEmail || `user #${entry.actorUserId}`,
        admin: true,
      })),
      ...(userActivity ?? []).map((entry) => ({
        key: `u-${entry.id}`,
        created: entry.created,
        label: userActivityLabel(entry.action),
        detail: userActivityDescription(entry),
        actor: entry.actorEmail || `user #${entry.actorUserId}`,
        admin: entry.isAdminAction,
      })),
    ]
      .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
      .slice(0, 6);
    return (
      <div className="admin-edit-user__block">
        <div className="admin-edit-user__card-head">
          <div className="admin-edit-user__card-title">Recent activity</div>
          <Button
            className="btn btn--sm btn--secondary"
            type="button"
            onClick={() => openDrawer('admin')}
          >
            <span className="ico--activity ico--xs" />
            Show all
          </Button>
        </div>
        <div className="admin-edit-user__card admin-edit-user__recent">
          {auditLog === null || userActivity === null ? (
            <Loader />
          ) : recent.length === 0 ? (
            <EmptyPanel
              size="xs"
              textKey="Nothing recorded yet"
              descriptionKey="Admin changes and the client's own actions will show up here."
            />
          ) : (
            <ul className="admin-edit-user__recent-list">
              {recent.map((item) => (
                <li key={item.key} className="admin-edit-user__recent-item">
                  <div className="admin-edit-user__recent-head">
                    <span className="admin-edit-user__recent-label">
                      {item.label}
                      {item.admin && <span className="admin-edit-user__audit-badge">admin</span>}
                    </span>
                    <span className="admin-edit-user__audit-date">
                      {format(new Date(item.created), 'yyyy-MM-dd HH:mm')}
                    </span>
                  </div>
                  <div className="admin-edit-user__recent-detail">
                    {item.detail ? `${item.detail} · ` : ''}
                    {item.actor}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  const renderAdminNotes = () => (
    <div className="admin-edit-user__admin-note">
      <div className="admin-edit-user__admin-note-form">
        <textarea
          className="admin-edit-user__admin-note-textarea"
          value={newNoteContent}
          onChange={(e) => setNewNoteContent(e.target.value)}
          placeholder="Internal note visible to admins only"
          maxLength={5000}
          rows={2}
          disabled={!canEdit}
        />
        {canEdit && (
          <div className="admin-edit-user__admin-note-actions">
            <Button
              className="btn btn--sm"
              label={isAddingNote ? 'Adding…' : 'Add note'}
              type="button"
              disabled={isAddingNote || newNoteContent.trim().length === 0}
              onClick={handleAddNote}
            />
          </div>
        )}
      </div>

      {adminNotes.length === 0 ? (
        <div className="admin-edit-user__admin-note-empty">No notes yet.</div>
      ) : (
        <>
          <ul className="admin-edit-user__admin-note-list">
            {(showAllNotes ? adminNotes : adminNotes.slice(0, NOTES_INITIAL_VISIBLE)).map((note) => {
              const isEditing = editingNoteId === note.id;
              return (
                <li key={note.id} className="admin-edit-user__admin-note-item">
                  <div className="admin-edit-user__admin-note-meta">
                    <span className="admin-edit-user__admin-note-author">
                      {note.authorEmail || 'unknown'}
                    </span>
                    <span className="admin-edit-user__admin-note-date">
                      {format(new Date(note.created), 'yyyy-MM-dd HH:mm')}
                      {note.updatedAt && (
                        <span className="admin-edit-user__admin-note-edited">
                          {' '}
                          · edited {format(new Date(note.updatedAt), 'yyyy-MM-dd HH:mm')}
                        </span>
                      )}
                    </span>
                    {!isEditing && canEdit && (
                      <div className="admin-edit-user__admin-note-row-actions">
                        <button
                          type="button"
                          className="action-btn"
                          title="Edit"
                          onClick={() => handleStartEditNote(note)}
                        >
                          <span className="ico--edit ico--xs" />
                        </button>
                        <button
                          type="button"
                          className="action-btn"
                          title="Delete"
                          onClick={() => handleDeleteNote(note.id)}
                        >
                          <span className="ico--trash ico--xs" />
                        </button>
                      </div>
                    )}
                  </div>
                  {isEditing ? (
                    <div className="admin-edit-user__admin-note-edit">
                      <textarea
                        className="admin-edit-user__admin-note-textarea"
                        value={editingContent}
                        onChange={(e) => setEditingContent(e.target.value)}
                        maxLength={5000}
                        rows={3}
                        autoFocus
                      />
                      <div className="admin-edit-user__admin-note-edit-actions">
                        <Button
                          className="btn btn--sm btn--secondary"
                          label="Cancel"
                          type="button"
                          onClick={handleCancelEditNote}
                          disabled={isSavingEdit}
                        />
                        <Button
                          className="btn btn--sm"
                          label={isSavingEdit ? 'Saving…' : 'Save'}
                          type="button"
                          onClick={handleSaveEditNote}
                          disabled={
                            isSavingEdit ||
                            editingContent.trim().length === 0 ||
                            editingContent === note.content
                          }
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="admin-edit-user__admin-note-content">{note.content}</div>
                  )}
                </li>
              );
            })}
          </ul>
          {!showAllNotes && adminNotes.length > NOTES_INITIAL_VISIBLE && (
            <div className="admin-edit-user__admin-note-show-more">
              <button
                type="button"
                className="admin-edit-user__admin-note-show-more-btn"
                onClick={() => setShowAllNotes(true)}
              >
                Show more ({adminNotes.length - NOTES_INITIAL_VISIBLE})
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="admin-edit-user">
      <ConfirmDialog className="dialog-box" />
      {toastElement}
      <PlanTemplatesDialog
        visible={templatesDialogOpen}
        onHide={() => setTemplatesDialogOpen(false)}
        canEdit={templatePermission.write}
        onChanged={setPlanTemplates}
        initialFeatures={selectedFeatures}
        initialAddons={selectedAddons}
      />

      {!canEdit && (
        <div className="admin-edit-user__readonly-banner">
          Read-only access — you can view this account but not edit it.
        </div>
      )}

      {/* Identity + account-level actions. */}
      <div className="admin-edit-user__header">
        <div className="admin-edit-user__identity">
          <div className="admin-edit-user__avatar">
            {initialsOf(userData.firstName, userData.lastName, userData.email)}
          </div>
          <div className="admin-edit-user__identity-text">
            <div className="admin-edit-user__name">
              {`${userData.firstName ?? ''} ${userData.lastName ?? ''}`.trim() || userData.email}
            </div>
            <div className="admin-edit-user__identity-meta">
              {userData.email} · accountId{' '}
              <button
                type="button"
                className="admin-edit-user__copy-id"
                onClick={() => copyToClipboard(String(id))}
                title={t('copy')}
              >
                #{id}
              </button>{' '}
              · since{' '}
              {userData.created ? format(new Date(userData.created), 'yyyy-MM-dd') : '-'}
            </div>
          </div>
        </div>
        <div className="admin-edit-user__header-actions">
          {accountResponse?.isSuperAdmin && canEdit && Number(id) !== accountResponse?.accountId && (
            <Button
              className="btn btn--sm admin-edit-user__impersonate-btn"
              type="button"
              onClick={handleImpersonate}
            >
              <span className="ico--password ico--xs" />
              {t('loginAsUser')}
            </Button>
          )}
          {userData.intercomUrl && (
            <Button className="btn btn--sm btn--secondary" type="button" onClick={handleOpenIntercom}>
              <span className="ico--external-link ico--xs" />
              Open in Intercom
            </Button>
          )}
          {canEdit && Number(id) !== accountResponse?.accountId && (
            <Button
              className={`btn btn--sm ${
                isBanned ? 'admin-edit-user__unban-btn' : 'admin-edit-user__ban-btn'
              }`}
              type="button"
              onClick={handleToggleBan}
            >
              <span className={`${isBanned ? 'ico--circle-check' : 'ico--close'} ico--xs`} />
              {isBanned ? 'Unban account' : 'Ban account'}
            </Button>
          )}
          {canEdit && isBanned && userData.bannedUntil && (
            <Button className="btn btn--sm btn--secondary" type="button" onClick={handleExtendBan}>
              <span className="ico--calendar ico--xs" />
              Extend 24 h
            </Button>
          )}
        </div>
      </div>

      {/* Phone: the same four actions in the bottom tab bar, in place of the default tabs. */}
      <MobileTabBarItems>
        <button type="button" className="mobile-tab-bar__item" onClick={() => openDrawer('notes')}>
          <span className="ico--message-square ico--sm mobile-tab-bar__icon" />
          <span className="mobile-tab-bar__label">Notes</span>
        </button>
        <button type="button" className="mobile-tab-bar__item" onClick={() => openDrawer('admin')}>
          <span className="ico--activity ico--sm mobile-tab-bar__icon" />
          <span className="mobile-tab-bar__label">History</span>
        </button>
        <button type="button" className="mobile-tab-bar__item" onClick={() => openDrawer('help')}>
          <span className="ico--question ico--sm mobile-tab-bar__icon" />
          <span className="mobile-tab-bar__label">Help</span>
        </button>
        <button type="button" className="mobile-tab-bar__item" onClick={onSwitchLayout}>
          <span className="ico--grid-3x3 ico--sm mobile-tab-bar__icon" />
          <span className="mobile-tab-bar__label">Classic</span>
        </button>
      </MobileTabBarItems>

      {/* Notes and logs live in a drawer opened from the tabs on the right edge — the page stays about the account. */}
      <div className="admin-side-tabs" style={{ right: sideTabsOffset }}>
        <button
          type="button"
          className="admin-side-tabs__tab"
          title="Admin notes"
          onClick={() => openDrawer('notes')}
        >
          <span className="ico ico--md ico--message-square admin-side-tabs__icon" />
          Notes{adminNotes.length > 0 ? ` (${adminNotes.length})` : ''}
        </button>
        <button
          type="button"
          className="admin-side-tabs__tab"
          title="History"
          onClick={() => openDrawer('admin')}
        >
          <span className="ico ico--md ico--activity admin-side-tabs__icon" />
          History
        </button>
        <button
          type="button"
          className="admin-side-tabs__tab"
          title="Help"
          onClick={() => openDrawer('help')}
        >
          <span className="ico ico--md ico--question admin-side-tabs__icon" />
          Help
        </button>
        <button
          type="button"
          className="admin-side-tabs__tab admin-side-tabs__tab--muted"
          title="Back to the classic account page"
          onClick={onSwitchLayout}
        >
          <span className="ico ico--md ico--grid-3x3 admin-side-tabs__icon" />
          Classic view
        </button>
      </div>
      <Sidebar
        visible={drawerOpen}
        onHide={() => setDrawerOpen(false)}
        position="right"
        showCloseIcon={false}
        className="admin-history"
        maskClassName="admin-history-mask"
        transitionOptions={{ classNames: 'admin-history-slide', timeout: 250 }}
      >
        <div className="admin-history__head">
          <div className="admin-edit-user__card-title">
            {drawerTab === 'notes' ? 'Admin notes' : drawerTab === 'help' ? 'Help' : 'History'}
          </div>
          <button
            type="button"
            className="action-btn"
            title={t('close')}
            onClick={() => setDrawerOpen(false)}
          >
            <span className="ico--close ico--xs" />
          </button>
        </div>
        <div className="admin-history__tabs">
          {(
            [
              ['notes', `Notes (${adminNotes.length})`],
              ['admin', `Admin changes${auditLog ? ` (${auditLog.length})` : ''}`],
              ['user', `User activity${userActivity ? ` (${userActivity.length})` : ''}`],
              ['help', 'Help'],
            ] as [DrawerTab, string][]
          ).map(([tab, label]) => (
            <button
              key={tab}
              type="button"
              className={`admin-history__tab${drawerTab === tab ? ' admin-history__tab--active' : ''}`}
              onClick={() => setDrawerTab(tab)}
            >
              {label}
            </button>
          ))}
        </div>
        <SimpleBar className="admin-history__scroll" autoHide={false}>
          {drawerTab === 'notes' && renderAdminNotes()}
          {drawerTab === 'admin' && renderAuditLog()}
          {drawerTab === 'user' && renderUserActivity()}
          {drawerTab === 'help' && <AccountPageHelp />}
        </SimpleBar>
      </Sidebar>

      {/* Counters — same tiles as the admin dashboard. */}
      <div className="stat-tiles admin-edit-user__tiles">
        <StatTile
          title="Account status"
          icon="ico--shield-check"
          value={
            <span
              className={`admin-edit-user__status-badge admin-edit-user__status-badge--${
                isBanned ? 'banned' : userData.accountStatus.toLowerCase()
              }`}
            >
              {isBanned ? 'Banned' : statusLabel}
            </span>
          }
          footer={
            isBanned
              ? `Banned since ${format(new Date(userData.bannedAt as string), 'yyyy-MM-dd')}${
                  userData.bannedReason === 'ABUSE' ? ', automatically (dangerous markup in input)' : ''
                }${
                  userData.bannedUntil
                    ? `, until ${format(new Date(userData.bannedUntil), 'yyyy-MM-dd HH:mm')}`
                    : ''
                }.`
              : STATUS_HINTS[userData.accountStatus] ?? ''
          }
        />
        <StatTile
          title="Projects"
          icon="ico--folder"
          value={userData.projectsCount}
          footer="Projects in the account."
        />
        <StatTile
          title="Users"
          icon="ico--password"
          value={`${userData.usersCount} / ${userData.maxUsers}`}
          footer="Users / user limit."
        />
        <StatTile
          title="Prompts"
          icon="ico--chat"
          value={
            isUnlimitedPrompts
              ? `${userData.monitorCount} / ∞`
              : `${userData.monitorCount} / ${userData.maxMonitorCount}`
          }
          footer={
            isUnlimitedPrompts ? (
              'Unlimited prompts.'
            ) : (
              <div className="admin-edit-user__usage">
                <div className="admin-edit-user__usage-bar">
                  <div
                    className={`admin-edit-user__usage-fill admin-edit-user__usage-fill--${promptUsageModifier}`}
                    style={{ width: `${Math.min(promptUsagePercent, 100)}%` }}
                  />
                </div>
                <span>{promptUsagePercent}% of the limit</span>
              </div>
            )
          }
        />
        {canSeeCosts && (
          <StatTile
            title="Cost this month"
            icon="ico--card"
            value={accountCosts ? formatUsd(accountCosts.totalUsd) : '-'}
            footer={
              accountCosts
                ? `Metered cost (${accountCosts.month.slice(0, 7)})${
                    accountCosts.projectedTotalUsd === null
                      ? ''
                      : `, ≈ ${formatUsd(accountCosts.projectedTotalUsd)} projected`
                  }.`
                : 'No cost data.'
            }
          />
        )}
      </div>

      {/* Left: everything editable (one <form> only for react-hook-form's registration — every block
          saves itself, see saveSection). Right: read-only context. */}
      <div className="admin-edit-user__columns">
        <form onSubmit={(e) => e.preventDefault()} className="admin-edit-user__column">
          <div className="admin-edit-user__block">
            <div className="admin-edit-user__card-head">
              <div className="admin-edit-user__card-title">Account data</div>
            </div>
            <fieldset disabled={!canEdit} className={fieldsetClass('account')}>
              <div className="admin-edit-user__fields">
                {renderField(
                  'Email',
                  <div className="admin-edit-user__input-with-action">
                    <InputText
                      onInput={clearApiError}
                      defaultValue={String(userData.email)}
                      {...register('email', { required: t('fieldIsRequired') })}
                      className={errors.email || apiError ? 'form-box__invalid' : ''}
                    />
                    <button
                      type="button"
                      className="action-btn"
                      title={t('copy')}
                      onClick={() => copyToClipboard(userData.email)}
                    >
                      <span className="ico--copy ico--xs" />
                    </button>
                  </div>,
                  undefined,
                  errors.email?.message as string | undefined
                )}
                {renderField(
                  'First name',
                  <InputText
                    onInput={clearApiError}
                    defaultValue={String(userData.firstName)}
                    {...register('firstName')}
                    className={errors.firstName || apiError ? 'form-box__invalid' : ''}
                  />
                )}
                {renderField(
                  'Last name',
                  <InputText
                    onInput={clearApiError}
                    defaultValue={String(userData.lastName)}
                    {...register('lastName')}
                    className={errors.lastName || apiError ? 'form-box__invalid' : ''}
                  />
                )}
                {renderField(
                  'Discount ID',
                  <InputText
                    onInput={clearApiError}
                    {...register('discountId')}
                    defaultValue={userData.discountId || ''}
                    placeholder="Optional"
                    className={apiError ? 'form-box__invalid' : ''}
                  />
                )}
                {renderField(
                  'Account status',
                  <Dropdown
                    onChange={(e) => setAccountStatus(e.value.type)}
                    value={accountStatusOpt.find((o) => o.type === accountStatus)}
                    className="dropdown"
                    optionLabel="name"
                    options={accountStatusOpt}
                    itemTemplate={(option) => <DropdownItem name={option.name} />}
                  />,
                  accountStatus ? STATUS_HINTS[accountStatus] : undefined
                )}
                {renderField(
                  'Account type',
                  <Dropdown
                    onChange={(e) => setAccountType(e.value.type)}
                    value={accountTypeOpt.find((o) => o.type === accountType)}
                    options={accountTypeOpt}
                    className="dropdown"
                    optionLabel="name"
                    itemTemplate={(option) => <DropdownItem name={option.name} />}
                    disabled={true}
                  />,
                  accountType && ACCOUNT_TYPE_HINTS[accountType]
                    ? `${ACCOUNT_TYPE_HINTS[accountType]} · set by the subscription`
                    : 'Set by the subscription.'
                )}
              </div>
              <div className="admin-edit-user__checks">
                <div className="form-box__checkbox">
                  <Checkbox
                    onChange={() => setHasBrandAccount(!hasBrandAccount)}
                    checked={hasBrandAccount}
                  />
                  <span>Has Brand24 account</span>
                </div>
                <div className="form-box__checkbox">
                  <Checkbox
                    onChange={() => setCustomSettings(!isCustomSettings)}
                    checked={isCustomSettings}
                  />
                  <span>Custom settings</span>
                </div>
                <div className="form-box__checkbox">
                  <Checkbox
                    onChange={() => setPaymentSuspended(!paymentSuspended)}
                    checked={paymentSuspended}
                  />
                  <span>{t('paymentSuspended')}</span>
                </div>
                <div className="form-box__checkbox">
                  <Checkbox
                    onChange={() => setSemrushProvider(!isSemrushProvider)}
                    checked={isSemrushProvider}
                  />
                  <span>Query models via SemRush (unsupported models stay on BrightData)</span>
                </div>
              </div>
              {renderSectionFooter('account')}
            </fieldset>
          </div>
          <div className="admin-edit-user__block">
            <div className="admin-edit-user__card-head">
              <div className="admin-edit-user__card-title">Limits &amp; schedule</div>
            </div>
            <fieldset disabled={!canEdit} className={fieldsetClass('limits')}>
              <div className="admin-edit-user__fields admin-edit-user__fields--three">
                {renderField(
                  'Question limit',
                  <InputText
                    onInput={clearApiError}
                    defaultValue={String(userData.maxMonitorCount)}
                    {...register('questionLimit', { required: t('fieldIsRequired') })}
                    className={errors.questionLimit || apiError ? 'form-box__invalid' : ''}
                  />,
                  undefined,
                  errors.questionLimit?.message as string | undefined
                )}
                {renderField(
                  'User limit',
                  <InputText
                    onInput={clearApiError}
                    defaultValue={String(userData.maxUsers)}
                    {...register('maxUsers', { required: t('fieldIsRequired') })}
                    className={errors.maxUsers || apiError ? 'form-box__invalid' : ''}
                  />,
                  undefined,
                  errors.maxUsers?.message as string | undefined
                )}
                {renderField(
                  'Trial expiration',
                  <div className="admin-edit-user__trial">
                    {/* The whole box is the picker: the Calendar's own input is stretched over it, invisible,
                        so a click anywhere opens the panel and the panel anchors under the box. */}
                    <div
                      className={`admin-edit-user__trial-picker ${
                        errors.trialDays || apiError ? 'form-box__invalid' : ''
                      }`}
                      title="Pick the end of the trial"
                    >
                      <span className="ico--calendar ico--xs admin-edit-user__trial-icon" />
                      <span className="admin-edit-user__trial-value">
                        {daysBetween} {daysBetween === 1 ? 'day' : 'days'}
                      </span>
                      <Calendar
                        ref={calendarRef}
                        value={range}
                        onChange={(e) => {
                          const endDate = (e.value as Date[])[1] || (e.value as Date[])[0];
                          setRange([new Date(userData.created), endDate]);
                          setRangeTouched(true);
                          if (endDate && calendarRef.current) {
                            calendarRef.current.hide();
                          }
                        }}
                        selectionMode="range"
                        readOnlyInput
                        hideOnRangeSelection
                        dateFormat="yy-mm-dd"
                        pt={{
                          root: { className: 'admin-edit-user__trial-calendar' },
                          panel: { className: 'calendar-panel' },
                        }}
                        minDate={new Date(userData.created)}
                      />
                    </div>
                    <InputText
                      value={
                        range?.[0] && range?.[1]
                          ? `${format(range[0], 'yyyy-MM-dd')} → ${format(range[1], 'yyyy-MM-dd')}`
                          : '—'
                      }
                      className="admin-edit-user__trial-dates"
                      disabled={true}
                      title="Trial period (created → end of trial)"
                    />
                  </div>,
                  'Trial length in days; the dates are the real period, from account creation to the end of the trial.'
                )}
                {renderField(
                  'GEO reports / month',
                  <InputText
                    onInput={clearApiError}
                    defaultValue={String(userData.maxGeoReportsPerMonth ?? '')}
                    {...register('maxGeoReportsPerMonth')}
                    className={apiError ? 'form-box__invalid' : ''}
                    placeholder="Default (trial: 3, paid: 5)"
                  />
                )}
                {renderField(
                  'AI Visibility reports / month',
                  <InputText
                    onInput={clearApiError}
                    defaultValue={String(userData.maxAiVisibilityReportsPerMonth ?? '')}
                    {...register('maxAiVisibilityReportsPerMonth')}
                    className={apiError ? 'form-box__invalid' : ''}
                    placeholder={`Empty = default for this account (${aiVisibilityDefaultLimit})`}
                  />,
                  // Follows the status dropdown above, so switching TRIAL <-> paid updates the hint.
                  <>
                    Empty = default for this account ({aiVisibilityDefaultLimit}
                    {accountStatus === 'TRIAL' ? ', trial' : ''}) · 0 = generating blocked · any other
                    number overrides the limit.
                  </>
                )}
                {renderField(
                  'Prompt Analyst reviews / month',
                  <InputText
                    onInput={clearApiError}
                    defaultValue={String(userData.maxPromptAnalysesPerMonth ?? '')}
                    {...register('maxPromptAnalysesPerMonth')}
                    className={apiError ? 'form-box__invalid' : ''}
                    placeholder="Empty = default (20)"
                  />,
                  'Empty = default (20) · 0 = blocked · any other number overrides the limit.'
                )}
                <div className="admin-edit-user__field admin-edit-user__field--wide">
                  <div className="form-box__label">Monitoring frequency</div>
                  <div className="admin-edit-user__schedule">
                    <Dropdown
                      onChange={(e) => setMonitoringPreset(e.value.preset)}
                      value={monitoringScheduleOpt.find((o) => o.preset === monitoringPreset)}
                      options={monitoringScheduleOpt}
                      className="dropdown"
                      optionLabel="name"
                      itemTemplate={(option) => <DropdownItem name={option.name} />}
                    />
                    <span title={syncScheduleTooltip}>
                      <Button
                        type="button"
                        className="btn btn--sm btn--secondary"
                        label={`Sync projects to ${savedScheduleName} (${projectsOffSchedule})`}
                        disabled={!canSyncSchedule}
                        loading={syncingSchedule}
                        onClick={syncProjectsSchedule}
                      />
                    </span>
                  </div>
                  <small className="form-box__hint">
                    Default frequency for new projects. Without the MANAGE_MONITORING_SCHEDULE addon it
                    also <strong>applies to every existing project</strong> on save; with the addon,
                    existing projects keep their own schedule.
                  </small>
                </div>
              </div>
              {renderSectionFooter('limits')}
            </fieldset>
          </div>
          {templatePermission.read && renderPlanTemplates()}
          <div className="admin-edit-user__block">
            <div className="admin-edit-user__card-head">
              <div className="admin-edit-user__card-title">
                AI models{' '}
                <span className="admin-edit-user__card-count">{selectedFeatures.length} on</span>
              </div>
              <div className="input-search-box">
                <InputText
                  value={modelFilter}
                  onChange={(e) => setModelFilter(e.target.value)}
                  placeholder="Search models..."
                  className="input-search"
                />
              </div>
            </div>
            <fieldset disabled={!canEdit} className={fieldsetClass('models')}>
              <SimpleBar className="admin-edit-user__options" autoHide={false}>
                {visibleModels.map((model) => {
                  const icon = getMonitorTypeIcon(model as MonitorType);
                  const meta = MODEL_CATALOG[model];
                  return (
                    <div key={model} className="admin-edit-user__option">
                      <Checkbox
                        checked={selectedFeatures.includes(model)}
                        onChange={(e) => {
                          if (e.checked) {
                            setSelectedFeatures((prev) => [...prev, model]);
                          } else {
                            setSelectedFeatures((prev) => prev.filter((f) => f !== model));
                          }
                        }}
                      />
                      {icon && <span className={`ico ico--sm ${icon} admin-edit-user__option-icon`} />}
                      <span className="admin-edit-user__option-text">
                        <span className="admin-edit-user__option-name">
                          {model}
                          {meta && (
                            <span className="admin-edit-user__option-provider">{meta.provider}</span>
                          )}
                        </span>
                        {meta && (
                          <span className="admin-edit-user__option-desc">{meta.description}</span>
                        )}
                      </span>
                    </div>
                  );
                })}
                {visibleModels.length === 0 && (
                  <div className="admin-edit-user__audit-empty">No model matches.</div>
                )}
              </SimpleBar>
              {renderSectionFooter('models')}
            </fieldset>
          </div>
          <div className="admin-edit-user__block">
            <div className="admin-edit-user__card-head">
              <div className="admin-edit-user__card-title">
                Addons <span className="admin-edit-user__card-count">{selectedAddons.length} on</span>
              </div>
              <div className="input-search-box">
                <InputText
                  value={addonFilter}
                  onChange={(e) => setAddonFilter(e.target.value)}
                  placeholder="Search addons..."
                  className="input-search"
                />
              </div>
            </div>
            <fieldset disabled={!canEdit} className={fieldsetClass('addons')}>
              <SimpleBar className="admin-edit-user__options" autoHide={false}>
                {visibleAddons.map((addon) => {
                  // An implied addon is granted by the server on save whatever the box says, so the box
                  // shows it ticked and locked instead of letting the admin untick something that comes back.
                  const implier = impliedBy(addon, selectedAddons);
                  return (
                    <div key={addon} className="admin-edit-user__option">
                      <Checkbox
                        checked={selectedAddons.includes(addon) || implier !== null}
                        disabled={implier !== null}
                        onChange={(e) => {
                          if (e.checked) {
                            setSelectedAddons((prev) => [...prev, addon]);
                          } else {
                            setSelectedAddons((prev) => prev.filter((f) => f !== addon));
                          }
                        }}
                      />
                      <span className="admin-edit-user__option-text">
                        <span className="admin-edit-user__option-name">{addon}</span>
                        <span className="admin-edit-user__option-desc">
                          {addonDescriptions[addon]}
                          {implier && ` Implied by ${implier}.`}
                        </span>
                      </span>
                    </div>
                  );
                })}
                {visibleAddons.length === 0 && (
                  <div className="admin-edit-user__audit-empty">No addon matches.</div>
                )}
              </SimpleBar>
              {renderSectionFooter('addons')}
            </fieldset>
          </div>
        </form>
        <div className="admin-edit-user__column">
          <div className="admin-edit-user__block">
            <div className="admin-edit-user__card-head">
              <div className="admin-edit-user__card-title">Subscription &amp; limits</div>
              {userData.subscriptionId && (
                <Button className="btn btn--sm btn--secondary" type="button" onClick={handleOpenPaddle}>
                  <span className="ico--external-link ico--xs" />
                  Open in Paddle
                </Button>
              )}
            </div>
            <div className="admin-edit-user__card">
              <div className="admin-edit-user__kv">
                <div className="admin-edit-user__kv-col">
                  {renderKv('Account type', userData.accountType)}
                  {renderKv('Billing cycle', formatBilling(userData.billingInterval))}
                  {renderKv(
                    'Discount',
                    `${userData.discount && userData.discount !== '-' ? userData.discount : '-'}${
                      userData.discountId ? ` (${userData.discountId})` : ''
                    }`
                  )}
                  {renderKv(
                    'Created',
                    userData.created ? format(new Date(userData.created), 'yyyy-MM-dd') : '-'
                  )}
                  {renderKv(
                    'End date',
                    userData.endsAt ? format(new Date(userData.endsAt), 'yyyy-MM-dd') : '-'
                  )}
                </div>
                <div className="admin-edit-user__kv-col">
                  {renderKv(
                    'Last login',
                    userData.lastLogin ? format(new Date(userData.lastLogin), 'yyyy-MM-dd') : '-'
                  )}
                  {renderKv('Login count', userData.loginCount ?? 0)}
                  {renderKv('Projects', userData.projectsCount)}
                  {renderKv(
                    'Prompts',
                    isUnlimitedPrompts
                      ? `${userData.monitorCount} / unlimited`
                      : `${userData.monitorCount} / ${userData.maxMonitorCount}`
                  )}
                  {renderKv('Users', `${userData.usersCount} / ${userData.maxUsers}`)}
                </div>
              </div>
              {userData.subscriptionId && (
                <div className="admin-edit-user__subscription-id">{userData.subscriptionId}</div>
              )}
            </div>
          </div>
          {canSeeCosts && (
            <div className="admin-edit-user__block">
              <div className="admin-edit-user__card-head">
                <div
                  className="admin-edit-user__card-title beta-header"
                  title="Beta — estimated from metered usage and list prices"
                >
                  Costs this month <span className="indicator indicator--beta">Beta</span>
                </div>
              </div>
              <div className="admin-edit-user__card">
                {accountCosts ? (
                  <>
                    <div className="admin-edit-user__kv">
                      <div className="admin-edit-user__kv-col">
                        {renderKv(
                          'Pays / month',
                          formatMoney(
                            accountCosts.billing?.monthlyMinor ?? null,
                            accountCosts.billing?.currencyCode ?? null
                          )
                        )}
                        {renderKv(
                          'Projected full month',
                          accountCosts.projectedTotalUsd === null
                            ? '-'
                            : `≈ ${formatUsd(accountCosts.projectedTotalUsd)}`
                        )}
                        {renderKv(
                          'Plan',
                          accountCosts.billing
                            ? accountCosts.billing.items
                                .map((i) => i.productSlug ?? i.priceId)
                                .join(', ') || '-'
                            : 'not synced'
                        )}
                      </div>
                      <div className="admin-edit-user__kv-col">
                        {renderKv(
                          `Costs (${accountCosts.month.slice(0, 7)})`,
                          formatUsd(accountCosts.totalUsd)
                        )}
                        {renderKv(
                          'Margin',
                          accountCosts.marginUsd === null ? '-' : formatUsd(accountCosts.marginUsd)
                        )}
                      </div>
                    </div>
                    {accountCosts.byProvider.length > 0 && (
                      <div className="admin-edit-user__providers">
                        {accountCosts.byProvider.map((p) => (
                          <div className="admin-edit-user__provider" key={p.key}>
                            <span className="admin-edit-user__provider-name">{p.key}</span>
                            <span className="admin-edit-user__provider-value">
                              {formatUsd(p.costUsd)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="admin-edit-user__audit-empty">No cost data.</div>
                )}
              </div>
            </div>
          )}
          {renderRecentActivity()}
          <div className="admin-edit-user__block">
            <div className="admin-edit-user__card-head">
              <div className="admin-edit-user__card-title">Invited users</div>
              {canEdit && (
                <Button className="btn btn--sm" type="button" onClick={() => inviteUser()}>
                  <span className="ico--add ico--xs" />
                  Add user
                </Button>
              )}
            </div>
            <div className="admin-edit-user__table">
              <DataTable
                stripedRows
                value={invitedUser}
                tableStyle={{ width: '100%' }}
                tableClassName="table"
                className="table-wrapper table-wrapper--stack"
                responsiveLayout="stack"
                emptyMessage={() => (
                  <EmptyPanel
                    size="xs"
                    textKey="No invited users"
                    descriptionKey="People added to this account with “Add user” will be listed here."
                  />
                )}
              >
                <Column
                  field="userId"
                  header="User ID"
                  headerClassName="table__header table__header--center"
                  bodyClassName="table__cell--center"
                  style={{ width: '45px' }}
                />
                <Column
                  field="email"
                  header="Email"
                  bodyClassName="table__cell--stack-title"
                  headerClassName="table__header"
                />
                <Column
                  field="role"
                  header="Role"
                  headerClassName="table__header"
                  style={{ width: '90px' }}
                />
                <Column
                  field="created"
                  header="Date"
                  body={(user: InvitedUserResponse) => format(user.created, 'yyyy-MM-dd')}
                  headerClassName="table__header table__header--center"
                  bodyClassName="table__cell--center"
                  style={{ width: '110px' }}
                />
              </DataTable>
            </div>
          </div>
          <div className="admin-edit-user__block">
            <div className="admin-edit-user__card-head">
              <div className="admin-edit-user__card-title">Projects</div>
            </div>
            <div className="admin-edit-user__table">
              <DataTable
                stripedRows
                paginator
                paginatorClassName="pagination"
                removableSort
                sortField="id"
                sortOrder={1}
                rows={50}
                rowsPerPageOptions={[10, 25, 50]}
                value={projectsForId}
                tableStyle={{ width: '100%' }}
                tableClassName="table"
                className="table-wrapper table-wrapper--stack"
                responsiveLayout="stack"
                emptyMessage={() => (
                  <EmptyPanel
                    size="xs"
                    textKey="No projects yet"
                    descriptionKey="The account has not created a project."
                  />
                )}
                currentPageReportTemplate={t('rowsNumber') + ':'}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              >
                <Column
                  field="id"
                  header="Project ID"
                  headerClassName="table__header table__header--center"
                  bodyClassName="table__cell--center"
                  style={{ width: '45px' }}
                />
                <Column
                  field="projectLabel"
                  header="Name"
                  bodyClassName="table__cell--stack-title"
                  body={(data) => (
                    <NavLink to={`${PROJECTS}/${data.id}`} target="_blank">
                      {data.label}
                    </NavLink>
                  )}
                  headerClassName="table__header"
                  style={{ width: '100px' }}
                />
                <Column
                  field="created"
                  header="Created"
                  body={(data) => format(data.created, 'yyyy-MM-dd / HH:mm')}
                  headerClassName="table__header table__header--center"
                  bodyClassName="table__cell--center"
                  style={{ width: '145px' }}
                />
              </DataTable>
            </div>
          </div>
          <div className="admin-edit-user__block">
            <div className="admin-edit-user__card-head">
              <div className="admin-edit-user__card-title">{t('apiKeys')}</div>
              {canEdit && (
                <Button className="btn btn--sm" type="button" onClick={() => addNewAccountApiKey()}>
                  <span className="ico--add ico--xs" />
                  {t('createApiKey')}
                </Button>
              )}
            </div>
            <div className="admin-edit-user__table">
              <DataTable
                stripedRows
                paginator
                paginatorClassName="pagination"
                removableSort
                sortField="created"
                sortOrder={-1}
                rows={50}
                rowsPerPageOptions={[10, 25, 50]}
                value={apiKeys}
                tableStyle={{ width: '100%' }}
                tableClassName="table"
                className="table-wrapper table-wrapper--stack"
                responsiveLayout="stack"
                emptyMessage={() => (
                  <EmptyPanel
                    size="xs"
                    textKey="No API keys"
                    descriptionKey="Keys created here or by the client in Settings will be listed."
                  />
                )}
                currentPageReportTemplate={t('rowsNumber') + ':'}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              >
                <Column
                  field="key"
                  header={t('apiKey')}
                  bodyClassName="table__cell--stack-title"
                  body={(apiKey: ApiKeyResponse) => (
                    <div className="admin-edit-user__api-key">
                      <span className="admin-edit-user__api-key-value">
                        {apiKey.key.substring(0, 20)}...
                      </span>
                      <Button
                        className="action-btn"
                        type="button"
                        onClick={() => copyToClipboard(apiKey.key)}
                        title={t('copy')}
                      >
                        <span className="ico--copy ico--xs" />
                      </Button>
                    </div>
                  )}
                  headerClassName="table__header"
                  style={{ minWidth: '100px' }}
                />
                <Column
                  field="description"
                  header={t('description')}
                  body={(apiKey: ApiKeyResponse) => apiKey.description || '-'}
                  headerClassName="table__header"
                />
                <Column
                  field="status"
                  header={t('status')}
                  body={(apiKey: ApiKeyResponse) => (
                    <span className={`status-${apiKey.status === 'ACTIVE' ? 'active' : 'inactive'}`}>
                      {apiKey.status === 'ACTIVE' ? t('active') : t('inactive')}
                    </span>
                  )}
                  headerClassName="table__header"
                  style={{ minWidth: '50px' }}
                />
                <Column
                  field="expires"
                  header={t('validity')}
                  sortable
                  body={(apiKey: ApiKeyResponse) => {
                    const { status, label } = getKeyStatus(apiKey.expires);
                    return <span className={`status-${status}`}>{label}</span>;
                  }}
                  headerClassName="table__header"
                  style={{ minWidth: '120px' }}
                />
              </DataTable>
            </div>
          </div>
          {(userData.apiClientId ||
            userData.apiExternalAccountId ||
            userData.apiExternalAccountSettings) && (
            <div className="admin-edit-user__block">
              <div className="admin-edit-user__card-head">
                <div className="admin-edit-user__card-title">{t('apiIntegration')}</div>
              </div>
              <div className="admin-edit-user__card">
                <div className="admin-edit-user__fields">
                  {userData.apiClientId &&
                    renderField(
                      t('apiClientName'),
                      <div className="form-box__value">
                        {userData.apiClientName || `ID: ${userData.apiClientId}`}
                      </div>
                    )}
                  {userData.apiExternalAccountId &&
                    renderField(
                      t('apiExternalAccountId'),
                      <div className="form-box__value">{userData.apiExternalAccountId}</div>
                    )}
                  {userData.apiExternalAccountSettings &&
                    renderField(
                      t('apiExternalAccountSettings'),
                      <pre className="form-box__value form-box__pre">
                        {JSON.stringify(userData.apiExternalAccountSettings, null, 2)}
                      </pre>
                    )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUsersEditPageV2;
